/**
 * RequireMembership — Hard lock for premium routes.
 *
 * Unlike SoftPaywallRoute (preview + sticky overlay), this renders a
 * full-screen "Choose a sanctuary to enter" splash and refuses to render
 * the protected page until the user has an active subscription tier.
 *
 * Pass-through rules:
 *   - admins always pass
 *   - any user with at least one active tier in `requiredTiers` (default:
 *     ANY active subscription) passes
 *
 * Anyone else sees the splash with a single CTA to /pricing (plus a
 * subtle "Sign in" link for unauthenticated visitors).
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../App';
import { FloatingParticles } from './FloatingParticles';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default access list — membership tiers unlock premium routes.
// One-time Oracle purchases unlock ONLY the Oracle (see ORACLE_TIERS).
const ANY_ACTIVE_TIER = [
  'seeker',
  'mystic',
  'sovereign',
  'bundle',
  'daily_transmissions',
  'dream_journal',
];

export const ORACLE_TIERS = [...ANY_ACTIVE_TIER, 'oracle_single', 'oracle_premium'];

export const RequireMembership = ({
  sanctuaryName,
  requiredTiers = ANY_ACTIVE_TIER,
  recommendedPackage = 'mystic',
  children,
}) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    (async () => {
      if (!user) {
        if (!cancelled) { setHasAccess(false); setLoading(false); }
        return;
      }
      if (user.is_admin) {
        if (!cancelled) { setHasAccess(true); setLoading(false); }
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/payments/my-subscription`, {
          credentials: 'include',
        });
        if (!res.ok) {
          if (!cancelled) setHasAccess(false);
          return;
        }
        const data = await res.json();
        const tiers = data?.active_tiers || [];
        if (!cancelled) {
          setHasAccess(tiers.some((t) => requiredTiers.includes(t)));
        }
      } catch {
        if (!cancelled) setHasAccess(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, authLoading, requiredTiers]);

  if (authLoading || loading) {
    return (
      <div
        className="min-h-screen hero-gradient flex items-center justify-center"
        data-testid="require-membership-loading"
      >
        <div className="lotus-aura lotus-aura-inner w-10 h-10 rounded-full animate-glow-pulse" />
      </div>
    );
  }

  if (hasAccess) return children;

  const nextPath = encodeURIComponent(location.pathname + location.search);
  const pricingHref = `/pricing?recommended=${encodeURIComponent(recommendedPackage)}`;

  return (
    <div
      data-testid="membership-required-splash"
      className="min-h-screen hero-gradient relative overflow-hidden flex items-center justify-center px-4 py-12"
    >
      <FloatingParticles count={40} />
      <div className="cosmic-mist absolute inset-0 pointer-events-none opacity-50 z-0" />
      <div className="stardust-layer absolute inset-0 pointer-events-none z-0" />

      <div className="relative z-10 w-full max-w-xl text-center">
        <div className="flex items-center justify-center gap-3 mb-7">
          <div className="w-12 h-px bg-gradient-to-r from-transparent to-[var(--accent-gold-dim)] opacity-50" />
          <Lock className="text-[var(--accent-gold)] opacity-80" size={20} />
          <div className="w-12 h-px bg-gradient-to-l from-transparent to-[var(--accent-gold-dim)] opacity-50" />
        </div>

        <p className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent-violet)] mb-4">
          Membership Required
        </p>

        <h1
          data-testid="membership-required-title"
          className="font-['Cormorant_Garamond'] text-4xl md:text-5xl text-[var(--text-primary)] glow-text-hero leading-[1.1] mb-5"
        >
          Choose a sanctuary to enter
        </h1>

        {sanctuaryName && (
          <p
            data-testid="membership-required-sanctuary"
            className="font-['Cormorant_Garamond'] text-lg md:text-xl italic text-[var(--text-secondary)] leading-relaxed max-w-md mx-auto mb-8"
          >
            <span className="text-[var(--text-accent)]">{sanctuaryName}</span> opens only to those who walk the sanctuary path.
          </p>
        )}

        <div className="reflection-note rounded-3xl px-6 py-7 md:px-8 md:py-8 mb-8 text-left">
          <p className="text-[10px] uppercase tracking-[0.35em] text-[var(--accent-violet)] mb-3 text-center">
            Why a gate
          </p>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Some writings, oracles, and frequencies are held quietly — offered only to those who choose to step inside. A sanctuary keeps its softness by being invited, not exposed.
          </p>
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            data-testid="membership-required-pricing-btn"
            onClick={() => navigate(pricingHref)}
            className="cinematic-cta px-9 py-4 rounded-full text-[var(--text-accent)] text-xs uppercase tracking-[0.25em] font-medium inline-flex items-center gap-3"
          >
            <Sparkles size={14} />
            Choose a Sanctuary
            <ArrowRight size={14} />
          </button>

          {!user && (
            <button
              data-testid="membership-required-signin-btn"
              onClick={() => navigate(`/lotus-gate?next=${nextPath}`)}
              className="text-[11px] uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors"
            >
              Already a soul here? Sign in
            </button>
          )}
          {user && !user.is_admin && (
            <button
              data-testid="membership-required-membership-btn"
              onClick={() => navigate('/membership')}
              className="text-[11px] uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors"
            >
              View my membership
            </button>
          )}
          <button
            data-testid="membership-required-home-btn"
            onClick={() => navigate('/')}
            className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)]/70 hover:text-[var(--text-muted)] transition-colors mt-2"
          >
            ← Return home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequireMembership;
